import 'styles/page/onboarding.page.css'

import React, { useState, useEffect, useRef } from 'react'
import CurrencyInput from 'react-currency-input-field'
import { ArrowLeftIcon, ArrowRightIcon, ArrowClockwiseIcon } from '@phosphor-icons/react'
import { useOnboarding } from 'context/onboarding.context'
import { CLASS_LABELS, CURRENCY_LONG, LOCALE } from 'utilities/global.variables'
import { FIRST_QUESTION, TOTAL_STEPS } from './onboarding.questions'



// ─── Page ───

const OnboardingPage = () => {
  const { setOnboarding, startOnboarding } = useOnboarding()

  // FLOW

  const [question, setQuestion] = useState(null)
  const [value, setValue] = useState('')
  const [answers, setAnswers] = useState({})
  const [history, setHistory] = useState([])

  // Returns a question's blank input value. Questions without an input are
  // breather/explainer screens.
  const initValue = (q, resolvedAnswers = answers) => {
    if (!q.input) return ''
    if (q.input.type === 'checkbox') return []
    if (['number-group', 'toggle-group', 'details-group', 'forecast-group'].includes(q.input.type)) {
      const options = typeof q.input.options === 'function' ? q.input.options(resolvedAnswers) : q.input.options
      if (q.input.type === 'toggle-group') return Object.fromEntries(options.map(opt => [opt, true]))
      if (q.input.type === 'details-group') return Object.fromEntries(options.map(opt => [opt, { interest_rate: '', term_years: '', term_months: '' }]))
      if (q.input.type === 'forecast-group') return Object.fromEntries(options.map(opt => [opt, { annual_contributions: '', annual_growth: '' }]))
      return Object.fromEntries(options.map(opt => [opt, '']))
    }
    return ''
  }

  // Sets the active question and initialises its input value.
  const selectNext = (q, resolvedAnswers = answers) => {
    setQuestion(q)
    setValue(initValue(q, resolvedAnswers))
  }

  const setNumberGroupValue = (opt, amount) => {
    setValue(prev => ({ ...prev, [opt]: amount }))
  }

  const toggleCheckbox = (opt) => {
    setValue(prev => prev.includes(opt) ? prev.filter(v => v !== opt) : [...prev, opt])
  }

  const setToggleValue = (opt, val) => {
    setValue(prev => ({ ...prev, [opt]: val }))
  }

  const setDetailValue = (opt, field, v) => {
    setValue(prev => ({ ...prev, [opt]: { ...prev[opt], [field]: v } }))
  }

  const selectBack = () => {
    if (history.length === 0) return
    const prev_q = history[history.length - 1]
    setHistory(prev => prev.slice(0, -1))
    setQuestion(prev_q)
    setValue(answers[prev_q.id] ?? initValue(prev_q))
  }

  // Skips the current question — advances without recording an answer.
  const selectSkip = () => {
    const next = resolveNext(question, value)
    if (!next) return
    setHistory(prev => [...prev, question])
    selectNext(next)
  }

  // next_q can be a plain reference or a function of the submitted value and
  // the answers so far (branching).
  const resolveNext = (q, val, ans = answers) => typeof q.next_q === 'function' ? q.next_q(val, ans) : q.next_q

  const canContinue = !question ? false
    : !question.input ? true
    : question.input.type === 'checkbox' ? value.length > 0
    : question.input.type === 'number-group' ? true
    : typeof value === 'string' ? value.trim() !== ''
    : true

  const handleContinue = e => {
    e.preventDefault()
    if (!canContinue) return

    const next = resolveNext(question, value)
    if (!next) return

    const nextAnswers = { ...answers, [question.id]: value }
    setAnswers(nextAnswers)

    if (question.onboarding_key) {
      setOnboarding(prev => ({ ...prev, [question.onboarding_key]: value }))
    }

    setHistory(prev => [...prev, question])
    selectNext(next, nextAnswers)
  }

  // Renders a group-type input's rows with a category heading per class,
  // looked up from the checkbox question the options came from (input.source_q).
  const renderGroupedRows = (renderRow) => {
    const options = typeof question.input.options === 'function' ? question.input.options(answers) : question.input.options
    const classOf = (label) => question.input.source_q().input.options.find(o => o.label === label)?.class
    let lastClass = null
    return options.map(opt => {
      const cls = classOf(opt)
      const showHeading = cls !== lastClass
      lastClass = cls
      return (
        <React.Fragment key={opt}>
          {showHeading && <p className='number-group-heading'>{CLASS_LABELS[cls]}</p>}
          {renderRow(opt)}
        </React.Fragment>
      )
    })
  }

  const formRef = useRef(null)

  // Enter submits from anywhere on the screen. Form controls already submit
  // natively; this covers breathers and the hidden-input pill buttons, where
  // focus sits on the body.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Enter') return
      const tag = e.target.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' || tag === 'BUTTON') return
      formRef.current?.requestSubmit()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const initialised = useRef(false)

  useEffect(() => {
    if (initialised.current) return
    initialised.current = true
    startOnboarding()
    selectNext(FIRST_QUESTION)
  }, [])

  const progress = question ? (question.step / TOTAL_STEPS) * 100 : 0

  return (
    <div className='OnboardingPage'>
      <div className='Status'>
        <button
          type='button'
          className={`BackButton ${history.length === 0 ? 'hidden' : ''}`}
          onClick={selectBack}
        >
          <ArrowLeftIcon size={22} weight='bold' />
        </button>
        <div className='bar-background'>
          <div className='bar-fill' style={{ width: `${progress}%` }} />
        </div>
        <button type='button' className='SkipButton' onClick={selectSkip}>
          <ArrowClockwiseIcon size={22} weight='bold' />
        </button>
      </div>

      <form className='Message' onSubmit={handleContinue} ref={formRef}>
        <p className='large bold SectionHeading'>{question?.section}</p>
        <p className='xxlarge Question'>
          {question && (typeof question.question === 'function' ? question.question(answers) : question.question)}
        </p>
        {question?.note && <p className='large o50 Note'>{question.note}</p>}

        <div className='OnboardingInput'>
          {question?.input?.type === 'text' && (
            <input
              type='text'
              className='text-answer'
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder={question.input.placeholder}
              autoFocus
              maxLength={30}
            />
          )}

          {question?.input?.type === 'number' && (
            <CurrencyInput
              className='text-answer number'
              placeholder={question.input.placeholder}
              disableAbbreviations={true}
              allowNegativeValue={false}
              allowDecimals={false}
              maxLength={question.input.maxLength}
              value={value}
              onValueChange={(v) => setValue(v ?? '')}
              autoFocus
            />
          )}

          {question?.input?.type === 'currency' && (
            <CurrencyInput
              className='text-answer number'
              intlConfig={{ locale: LOCALE, currency: CURRENCY_LONG.currency }}
              prefix='£'
              placeholder={question.input.placeholder}
              allowDecimals={false}
              allowNegativeValue={false}
              value={value}
              onValueChange={(v) => setValue(v ?? '')}
              autoFocus
            />
          )}

          {question?.input?.type === 'date' && (
            <input
              type='date'
              className='text-answer'
              min='1900-01-01'
              max={new Date().toISOString().split('T')[0]}
              value={value}
              onChange={e => setValue(e.target.value)}
            />
          )}

          {question?.input?.type === 'select' && (
            <select value={value} onChange={e => setValue(e.target.value)}>
              <option value='' disabled>{question.input.placeholder}</option>
              {question.input.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          )}

          {question?.input?.type === 'radio' && (
            <div className='button-stack flex col' role='group'>
              {question.input.options.map(opt => (
                <React.Fragment key={opt}>
                  <input
                    type='radio'
                    name={question.id}
                    id={opt}
                    checked={value === opt}
                    onChange={() => setValue(opt)}
                  />
                  <label htmlFor={opt}>{opt}</label>
                </React.Fragment>
              ))}
            </div>
          )}

          {question?.input?.type === 'checkbox' && (
            <div className='button-tag' role='group'>
              {question.input.options.map(opt => (
                <React.Fragment key={opt.label}>
                  <input
                    type='checkbox'
                    name={question.id}
                    id={opt.label}
                    checked={value.includes(opt.label)}
                    onChange={() => toggleCheckbox(opt.label)}
                  />
                  <label htmlFor={opt.label}>{opt.label}</label>
                </React.Fragment>
              ))}
            </div>
          )}

          {question?.input?.type === 'number-group' && (
            <div className='number-group flex col' role='group'>
              {renderGroupedRows(opt => (
                <div className='flex row space center'>
                  <label htmlFor={opt}>{opt}</label>
                  <CurrencyInput
                    id={opt}
                    intlConfig={{ locale: LOCALE, currency: CURRENCY_LONG.currency }}
                    className='number medium'
                    prefix='£'
                    placeholder='£0'
                    allowDecimals={false}
                    allowNegativeValue={false}
                    value={value[opt]}
                    onValueChange={(v) => setNumberGroupValue(opt, v ?? '')}
                  />
                </div>
              ))}
            </div>
          )}

          {question?.input?.type === 'toggle-group' && (
            <div className='toggle-group flex col' role='group'>
              {renderGroupedRows(opt => (
                <div className='flex row space center'>
                  <label>{opt}</label>
                  <div className='yes-no' role='radiogroup'>
                    <input
                      type='radio'
                      name={opt}
                      id={`${opt}-yes`}
                      checked={value[opt] === true}
                      onChange={() => setToggleValue(opt, true)}
                    />
                    <label htmlFor={`${opt}-yes`}>Yes</label>
                    <input
                      type='radio'
                      name={opt}
                      id={`${opt}-no`}
                      checked={value[opt] === false}
                      onChange={() => setToggleValue(opt, false)}
                    />
                    <label htmlFor={`${opt}-no`}>Nope</label>
                  </div>
                </div>
              ))}
            </div>
          )}

          {question?.input?.type === 'details-group' && (
            <div className='details-group flex col' role='group'>
              <div className='group-headings flex row space center'>
                <label></label>
                <div className='detail-inputs flex row center'>
                  <span className='col-heading rate-col'>Interest rate</span>
                  <span className='col-heading term-col'>Term</span>
                </div>
              </div>
              {renderGroupedRows(opt => (
                <div className='flex row space center'>
                  <label>{opt}</label>
                  <div className='detail-inputs flex row center'>
                    <CurrencyInput
                      id={`${opt}-rate`}
                      suffix='%'
                      className='number medium percent'
                      placeholder='1.2%'
                      allowNegativeValue={false}
                      allowDecimals={true}
                      decimalsLimit={2}
                      decimalScale={2}
                      maxLength={6}
                      value={value[opt]?.interest_rate}
                      onValueChange={(v) => setDetailValue(opt, 'interest_rate', v ?? '')}
                    />
                    <CurrencyInput
                      id={`${opt}-years`}
                      className='number medium term'
                      placeholder='0'
                      disableAbbreviations={true}
                      allowNegativeValue={false}
                      allowDecimals={false}
                      maxLength={2}
                      value={value[opt]?.term_years}
                      onValueChange={(v) => setDetailValue(opt, 'term_years', v ?? '')}
                    />
                    <span className='unit'>yrs</span>
                    <CurrencyInput
                      id={`${opt}-months`}
                      className='number medium term'
                      placeholder='0'
                      disableAbbreviations={true}
                      allowNegativeValue={false}
                      allowDecimals={false}
                      maxLength={2}
                      value={value[opt]?.term_months}
                      onValueChange={(v) => setDetailValue(opt, 'term_months', v !== undefined ? Math.min(Number(v), 11) : '')}
                    />
                    <span className='unit'>mths</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {question?.input?.type === 'forecast-group' && (
            <div className='forecast-group flex col' role='group'>
              <div className='group-headings flex row space center'>
                <label></label>
                <div className='detail-inputs flex row center'>
                  <span className='col-heading contributions-col'>Annual contributions</span>
                  <span className='col-heading growth-col'>Growth rate</span>
                </div>
              </div>
              {renderGroupedRows(opt => (
                <div className='flex row space center'>
                  <label>{opt}</label>
                  <div className='detail-inputs flex row center'>
                    <CurrencyInput
                      id={`${opt}-contributions`}
                      intlConfig={{ locale: LOCALE, currency: CURRENCY_LONG.currency }}
                      className='number medium contributions'
                      prefix='£'
                      placeholder='£0'
                      allowDecimals={false}
                      allowNegativeValue={false}
                      value={value[opt]?.annual_contributions}
                      onValueChange={(v) => setDetailValue(opt, 'annual_contributions', v ?? '')}
                    />
                    <span className='unit'>/yr</span>
                    <CurrencyInput
                      id={`${opt}-growth`}
                      suffix='%'
                      className='number medium percent'
                      placeholder='2%'
                      allowNegativeValue={false}
                      allowDecimals={true}
                      decimalsLimit={2}
                      decimalScale={2}
                      maxLength={3}
                      value={value[opt]?.annual_growth}
                      onValueChange={(v) => setDetailValue(opt, 'annual_growth', v ?? '')}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!question?.end && (
          <button type='submit' className='ContinueButton' disabled={!canContinue}>
            <ArrowRightIcon size={20} weight='bold' />
            {/* {question?.button ?? 'Continue'} */}
          </button>
        )}
      </form>
    </div>
  )
}

export default OnboardingPage
